import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { EASE } from "./Reveal";

const NODES = [
  { x: 260, y: 60, r: 7 },
  { x: 105, y: 145, r: 5.5 },
  { x: 415, y: 135, r: 5.5 },
  { x: 60, y: 305, r: 5 },
  { x: 460, y: 300, r: 5 },
  { x: 150, y: 415, r: 5.5 },
  { x: 370, y: 420, r: 5.5 },
  { x: 260, y: 250, r: 9 },
];

const LINKS = [
  [7, 0], [7, 1], [7, 2], [7, 3], [7, 4], [7, 5], [7, 6],
  [0, 1], [0, 2], [1, 5], [2, 6], [3, 5], [4, 6],
];

const CARDS = [
  { title: "VP — Engineering", sub: "Bengaluru · Technology", initials: "VE", cls: "-top-2 right-2", delay: 0.9 },
  { title: "Chief Product Officer", sub: "Mumbai · Leadership", initials: "CP", cls: "top-[38%] -left-6", delay: 1.15 },
  { title: "Head — Supply Chain", sub: "Gurugram · Operations", initials: "SC", cls: "-bottom-3 right-10", delay: 1.4 },
];

function Dossier({ title, sub, initials, cls, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay, duration: 0.7, ease: EASE }}
      className={`absolute z-10 ${cls}`}
    >
      <motion.div
        animate={{ y: [0, -9, 0] }}
        transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay }}
        className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0A0F1D]/90 px-4 py-3 shadow-[0_16px_50px_rgba(0,0,0,0.5)] backdrop-blur-md"
      >
        <span className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-sky-400/30 to-indigo-500/30 text-[11px] font-bold text-sky-300">
          {initials}
        </span>
        <span>
          <span className="block text-xs font-semibold text-white">{title}</span>
          <span className="block text-[10px] tracking-wide text-slate-400">{sub}</span>
        </span>
        <span className="ml-1 h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.9)]" />
      </motion.div>
    </motion.div>
  );
}

export default function TalentNetwork() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 45, damping: 18 });
  const sy = useSpring(my, { stiffness: 45, damping: 18 });
  const layerX = useTransform(sx, [-0.5, 0.5], [-16, 16]);
  const layerY = useTransform(sy, [-0.5, 0.5], [-12, 12]);
  const glowX = useTransform(sx, [-0.5, 0.5], [24, -24]);
  const glowY = useTransform(sy, [-0.5, 0.5], [18, -18]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div
      data-testid="talent-network-visual"
      className="relative mx-auto aspect-[13/12] w-full max-w-[540px]"
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
    >
      <motion.div
        style={{ x: glowX, y: glowY }}
        className="absolute inset-8 rounded-full bg-[radial-gradient(circle,rgba(56,189,248,0.16),rgba(99,102,241,0.07)_55%,transparent_75%)] blur-2xl"
      />
      <motion.div style={{ x: layerX, y: layerY }} className="absolute inset-0">
        <svg viewBox="0 0 520 480" className="h-full w-full" fill="none" aria-hidden="true">
          <defs>
            <linearGradient id="lineGrad" x1="0" y1="0" x2="520" y2="480" gradientUnits="userSpaceOnUse">
              <stop stopColor="#38BDF8" stopOpacity="0.55" />
              <stop offset="1" stopColor="#6366F1" stopOpacity="0.4" />
            </linearGradient>
            <radialGradient id="nodeGrad">
              <stop offset="0%" stopColor="#7DD3FC" />
              <stop offset="100%" stopColor="#38BDF8" />
            </radialGradient>
          </defs>
          {LINKS.map(([a, b], i) => {
            const p1 = NODES[a];
            const p2 = NODES[b];
            return (
              <g key={i}>
                <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="url(#lineGrad)" strokeWidth="1" />
                <circle r="2.4" fill="#7DD3FC">
                  <animateMotion dur={`${3.4 + (i % 5) * 0.9}s`} repeatCount="indefinite" path={`M ${p1.x} ${p1.y} L ${p2.x} ${p2.y}`} />
                </circle>
              </g>
            );
          })}
          {NODES.map((n, i) => (
            <g key={i} className="node-pulse" style={{ animationDelay: `${i * 0.35}s` }}>
              <circle cx={n.x} cy={n.y} r={n.r * 2.4} fill="#38BDF8" opacity="0.08" />
              <circle cx={n.x} cy={n.y} r={n.r * 1.5} fill="#38BDF8" opacity="0.16" />
              <circle cx={n.x} cy={n.y} r={n.r} fill="url(#nodeGrad)" />
            </g>
          ))}
        </svg>
      </motion.div>
      {CARDS.map((c) => (
        <Dossier key={c.title} {...c} />
      ))}
    </div>
  );
}
